import { Box, Chip, CircularProgress, FormControl, Grid, InputLabel, Paper, NativeSelect, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LiveClock from "react-live-clock";
import { useParams } from "react-router-dom";
import { UserDataContext } from "../app/App";
import Layout from "../components/Layout/Layout";
import LoadingButton from "../components/LoadingButton/LoadingButton";
import { StandardBackToolbar } from "../components/Toolbar/StandardBackToolbar";
import { useEmployeeTimeClock } from "../hooks";

export default function TimeClock() {
    const { id, name } = useParams();
    const { user } = useContext(UserDataContext);
    const { t } = useTranslation();

    const employeeId = id ?? user?.id;
    const employeeName = name ?? user?.name;

    // Get employee time entry data
    const { timeClockDetails, timeClockCategories, isLoading, isError, startTimeClock, isLoadingStartTimeClock, stopTimeClock, isLoadingStopTimeClock } = useEmployeeTimeClock({
        employeeId,
        employeeName,
    });

    const [selectedCategory, setSelectedCategory] = useState("");

    // Set selected category from current time entry, if present
    useEffect(() => {
        setSelectedCategory(timeClockDetails?.categorySelected ?? "");
    }, [timeClockDetails?.categorySelected]);

    const handleUpdateTimeClock = (actionType) => {
        if (!selectedCategory) return;

        if (actionType === "start") {
            startTimeClock(employeeId, selectedCategory);
        } else if (actionType === "stop") {
            stopTimeClock(employeeId, selectedCategory);
        }
    };

    const handleCategoryChange = (event) => {
        setSelectedCategory(event.target.value);
    };

    // Loading & error pages
    if (isLoading) {
        return (
            <Layout title={employeeName} color="rgb(141,198,63)">
                <Box display="flex" justifyContent="center" mt={3}>
                    <CircularProgress size={50} />
                </Box>
            </Layout>
        );
    }
    if (isError) {
        return <Typography>Error loading time clock data</Typography>;
    }

    const clockAction = timeClockDetails?.hasClockedIn ? "stop" : "start";
    const isButtonDisabled = isLoadingStartTimeClock || isLoadingStopTimeClock || !selectedCategory;
    const isKiosk = user?.crmPrivileges.some((p) => p.name.toLowerCase() === "kiosk");
    console.log("user", user);
    console.log("privileges", user?.crmPrivileges);

    return (
        <Layout
            title={employeeName}
            color="rgb(141,198,63)"
            extra={<StandardBackToolbar currentUser={{ name: employeeName, id: employeeId }} report={!isKiosk} loginOverview={user?.crmPrivileges.some((p) => p.name === "Stimpilklukka super")} />}
        >
            <Grid container spacing={3}>
                {/* Time Entry Details */}
                <Grid item xs={12}>
                    <Box component={Paper} variant="outlined" elevation={0} borderRadius={15} p={1} display="flex" flexDirection="column" alignItems="center">
                        <Typography variant="h4" gutterBottom>
                            <LiveClock format="HH:mm:ss" ticking />
                        </Typography>
                        <Box display="flex" style={{ gap: 10, alignItems: "center" }}>
                            <Typography variant="h6">
                                {timeClockDetails?.hasClockedIn
                                    ? `🟢 Clocked in: ${timeClockDetails.startDateFormatted}`
                                    : `🔴 Clocked out${timeClockDetails?.endDateFormatted ? `: ${timeClockDetails?.endDateFormatted}` : ""}`}
                            </Typography>
                            {timeClockDetails?.hasClockedIn && timeClockDetails?.categorySelected && <Chip label={timeClockDetails?.categorySelected} size="small" variant="outlined" />}
                        </Box>
                    </Box>
                </Grid>
                {/* Categories Select */}
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <InputLabel variant="standard" htmlFor="category-select">
                            Category
                        </InputLabel>
                        <NativeSelect
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                            inputProps={{
                                name: "age",
                                id: "category-select",
                            }}
                            sx={{
                                width: "100%",
                                borderRadius: "4px",
                                border: "1px solid lightgray",
                                padding: "0.75rem",
                            }}
                        >
                            {timeClockCategories?.map((category) => (
                                <option
                                    key={category.id}
                                    value={category.name}
                                    style={{
                                        backgroundColor: "white",
                                    }}
                                >
                                    {category.name}
                                </option>
                            ))}
                        </NativeSelect>
                    </FormControl>
                </Grid>
                {/* Clock In & Clock Out Button */}
                <Grid item xs={12} style={{ paddingTop: "6rem" }}>
                    <LoadingButton
                        onClick={() => handleUpdateTimeClock(clockAction)}
                        disabled={isButtonDisabled}
                        loading={isLoadingStartTimeClock || isLoadingStopTimeClock}
                        variant="outlined"
                        size="large"
                        fullWidth
                        style={{
                            borderRadius: 50,
                            height: "50px",
                            fontSize: "20px",
                            backgroundColor: isLoadingStartTimeClock || isLoadingStopTimeClock ? "gray" : timeClockDetails?.hasClockedIn ? "#dc3545" : "#28a745",
                            color: "#fff",
                        }}
                    >
                        {timeClockDetails?.hasClockedIn ? t("CheckOut") : t("CheckIn")}
                    </LoadingButton>
                </Grid>
            </Grid>
        </Layout>
    );
}
